import { isPrerendering } from 'services/prerender'
import { ErrorSeverity, GraphQLError, IAPIError } from 'types/error'
import { asError, getErrorCode, getErrorExtraInfo, getErrorName, getErrorSeverity, getErrorText, objectToError } from 'services/error'
import { NextPageContext } from 'next'
import { graphql, readInlineData } from 'react-relay'
import { analytics_init$key } from '__generated__/analytics_init.graphql'
import { analytics_setUserInfo$key } from '__generated__/analytics_setUserInfo.graphql'
import logger from 'services/logger'
import { SeverityLevel } from '@sentry/nextjs'
import { extractQueryParams } from './url'
import { getBuildID } from './versioning'

// NOTE: the webpack chunk names here are DELIBERATELY WRONG
// The reason is that Ad Block type extensions will block the request
// for this script if the name contains "mixpanel"
// Also, the Service Worker WILL NOT INSTALL if the user has ad block
// because it tries to precache this .js bundle and the ad block prevents that
// if the bundle contains the name of a tracking tool in the file name.

async function getMixpanel() {
  return import(/* webpackChunkName: "Mapelpix" */'mixpanel-browser')
}

async function getSentry() {
  // NOTE: webpack chunk name DELIBERATELY wrong. See comment at start of file
  return import(/* webpackChunkName: "Turret" */'@sentry/nextjs')
}

async function getLogRocket() {
  // NOTE: webpack chunk name DELIBERATELY wrong. See comment at start of file
  return import(/* webpackChunkName: "Missile" */'logrocket').then(m => m.default)
}

async function getLogRocketReact() {
  // NOTE: webpack chunk name DELIBERATELY wrong. See comment at start of file
  return import(/* webpackChunkName: "MissileReact" */'logrocket-react').then(m => m.default)
}

async function getLogRocketSessionURL() {
  if (isPrerendering() || !process.env.NEXT_PUBLIC_LOGROCKET_APP_ID) return null

  const sessionURL = (window as any).logrocketsessionurl
  if (sessionURL) return sessionURL

  const logrocket = await getLogRocket()
  return new Promise(resolve => logrocket.getSessionURL((sessionUrl) => {
    (window as any).logrocketsessionurl = sessionUrl
    resolve(sessionUrl)
  }))
}

export async function setUserInfo(userFragmentRef: analytics_setUserInfo$key) {
  const currentUser = readInlineData(graphql`
    fragment analytics_setUserInfo on User @inline {
      name
      email
      unconfirmedEmail
      rawId
      
      realUser {
        rawId
        name
        email
        unconfirmedEmail
      }
    }
  `, userFragmentRef)
  const user = currentUser?.realUser || currentUser
  const mask = currentUser?.realUser ? currentUser : null
  try {
    if (process.env.NEXT_PUBLIC_SENTRY_DSN && user) {
      const Sentry = await getSentry()
      Sentry.setUser({
        id: user?.rawId ? `${user.rawId}` : undefined,
        name: user?.name || undefined,
        email: user?.email || user?.unconfirmedEmail || undefined,
        mask_id: mask?.rawId || undefined,
        mask_name: mask?.name || undefined,
        mask_email: mask?.email || mask?.unconfirmedEmail || undefined,
      })
    }
  } catch (error) {
    logger.error(error)
  }
  try {
    if (!isPrerendering() && process.env.NEXT_PUBLIC_GA_TRACKING_ID && user) {
      const w = window as any
      if (w.gtag) {
        w.gtag('config', 'GA_MEASUREMENT_ID', {
          user_id: user?.rawId,
        })
      }
    }
  } catch (error) {
    logger.error(error)
  }
  try {
    if (!isPrerendering() && process.env.NEXT_PUBLIC_MIXPANEL_TOKEN && user) {
      const mixpanel = await getMixpanel()
      mixpanel.identify(user?.rawId ? `${user.rawId}` : undefined)
      mixpanel.people.set({
        name: user?.name,
        email: user?.email,
      })
    }
  } catch (error) {
    logger.error(error)
  }
  try {
    if (!isPrerendering() && process.env.NEXT_PUBLIC_LOGROCKET_APP_ID && user?.rawId) {
      const logrocket = await getLogRocket()
      logrocket.identify(`${user.rawId}`, {
        // only name and email are "defaults", the rest is considered extra data by LogRocket
        // NOTE: all this data needs to be the same on all the user's sessions!
        name: user.name || '',
        email: user.email || user.unconfirmedEmail || '',
      })
    }
  } catch (error) {
    logger.error(error)
  }
}

export async function init(userFragmentRef: analytics_init$key | null) {
  const user = readInlineData(graphql`
    fragment analytics_init on User @inline {
      isFlexhireTeam
      
      realUser {
        isFlexhireTeam
      }
      
      ...analytics_setUserInfo
    }
  `, userFragmentRef)

  let logRocketSessionURL = null
  const shouldEnableLogrocket = !user?.realUser?.isFlexhireTeam

  if (!isPrerendering() && process.env.NEXT_PUBLIC_LOGROCKET_APP_ID && shouldEnableLogrocket) {
    try {
      const logrocket = await getLogRocket()
      logrocket.init(process.env.NEXT_PUBLIC_LOGROCKET_APP_ID, {
        release: getBuildID(),
        network: {
          requestSanitizer: (request) => {
            try {
              const query = extractQueryParams(request.url)
              if (query.password || query.password_confirmation) return null

              if (request.body?.indexOf('password')) return null

              return request
            } catch (error) {
              logger.error(error)
            }
            return null
          },
        },
      })
      const w = window as any
      w.logrocket = logrocket
      const logrocketReact = await getLogRocketReact()
      logrocketReact(logrocket)
      logRocketSessionURL = await getLogRocketSessionURL()
    } catch (error) {
      logger.error(error)
    }
  }

  if (process.env.NEXT_PUBLIC_SENTRY_DSN) {
    try {
      const Sentry = await getSentry()
      Sentry.setTag('ssr', isPrerendering())
      if (logRocketSessionURL) Sentry.setTag('LogRocket', logRocketSessionURL)
      if (!isPrerendering()) {
        const w = window as any
        w.Sentry = Sentry
      }
    } catch (err) {
      logger.error(err)
    }
  }

  if (!isPrerendering()) {
    if (process.env.NEXT_PUBLIC_MIXPANEL_TOKEN) {
      try {
        const mixpanel = await getMixpanel()
        mixpanel.init(process.env.NEXT_PUBLIC_MIXPANEL_TOKEN)
        const w = window as any
        w.mixpanel = mixpanel
        if (logRocketSessionURL) mixpanel.track('LogRocket', { logRocketSessionURL })
      } catch (error) {
        logger.error(error)
      }
    }

    if (process.env.NEXT_PUBLIC_GA_TRACKING_ID && (window as any).gtag && logRocketSessionURL) {
      (window as any).gtag('send', {
        hitType: 'event',
        eventCategory: 'LogRocket',
        eventAction: logRocketSessionURL,
      })
    }
  }

  if (user) setUserInfo(user)
}

export async function logPageView(url: string) {
  // https://developers.google.com/analytics/devguides/collection/gtagjs/pages
  if (process.env.NEXT_PUBLIC_GA_TRACKING_ID) {
    try {
      const w = window as any
      if (w.gtag) {
        w.gtag('config', process.env.NEXT_PUBLIC_GA_TRACKING_ID, {
          page_path: url,
        })
      }
    } catch (error) {
      logger.error(error)
    }
  }
}

export async function trackEvent(name: string, data?: any) {
  if (process.env.NEXT_PUBLIC_SENTRY_DSN) {
    const Sentry = await getSentry()
    Sentry.addBreadcrumb({
      message: name,
      data,
    })
  }
  if (!isPrerendering()) {
    if (process.env.NEXT_PUBLIC_MIXPANEL_TOKEN) {
      try {
        const mixpanel = await getMixpanel()
        mixpanel.track(name, data)
      } catch (error) {
        logger.error(error)
      }
    }
    if (process.env.NEXT_PUBLIC_GA_TRACKING_ID) {
      try {
        const w = window as any
        if (w.gtag) {
          w.gtag('event', name)
        }
      } catch (error) {
        logger.error(error)
      }
    }
    if (process.env.NEXT_PUBLIC_LOGROCKET_APP_ID) {
      try {
        const logrocket = await getLogRocket()
        logrocket.track(name)
      } catch (error) {
        logger.error(error)
      }
    }
  }
}

export function ctxErrorInfo(ctx: NextPageContext) {
  if (ctx) {
    return {
      url: ctx?.req?.url,
      method: ctx?.req?.method,
      headers: ctx?.req?.headers,
      query: ctx?.query,
    }
  }
  return {}
}

export function shouldTrackError(error: Error | IAPIError | GraphQLError) {
  try {
    // Disable these errors because they are not user facing issues and they cause spam on Sentry.
    const errorText = getErrorText(error)
    if (errorText?.includes('Unexpected Error') || errorText?.includes('Network Error')) return false
    if (['TypeError', 'RRNLRetryMiddlewareError'].includes(getErrorName(error))) return false

    // Disable logrocket react error because it does not support React 19 so it spams this error even though it works
    // TODO: upgrade to a version of logrocket-react that supports React 19 and remove this check
    if (errorText?.includes('logrocket-react caught an error while')) return false

    // If the error message matches this regexp, then it's a RelayNetwork error and it means the query we made
    // was wrong (didn't respect schema) or something else that's fatal for the frontend happened.
    // We need to handle it explicitly because otherwise the HTTP response code is 400 and it gets ignored.
    if (errorText?.match(/^No data returned/)) return true

    // Disable these errors because they are not user facing issues and they cause spam on Sentry.

    const errorCode = getErrorCode(error)

    // Ignore some error types such as bad request, unauthorized, and unprocessable entity
    if (errorCode && [400, 401, 403, 422].includes(errorCode)) return false

    return true
  } catch (raisedError) {
    console.error('Failed to determine if error should be tracked', raisedError)

    return true
  }
}

function severityToLevel(severity: ErrorSeverity): SeverityLevel {
  if (severity === 'low') return 'warning'
  if (severity === 'high') return 'fatal'

  return 'error'
}

export async function trackError(
  errorLike: unknown,
  severityOverride: ErrorSeverity | null = null,
  extraInfo: any = {},
) {
  const error = asError(errorLike)

  logger.error(error)
  let sentryErrorId = null
  const severity = severityOverride || getErrorSeverity(error) || 'medium'

  let allExtraInfo = {
    ...extraInfo,
    ...getErrorExtraInfo(error),
    severity,
  }
  const logRocketSessionURL = await getLogRocketSessionURL()
  if (logRocketSessionURL) allExtraInfo.logRocketSessionURL = logRocketSessionURL

  if (shouldTrackError(error) && process.env.NEXT_PUBLIC_SENTRY_DSN) {
    const Sentry = await getSentry()

    sentryErrorId = await new Promise((resolve, reject) => {
      try {
        Sentry.withScope((scope) => {
          try {
            scope.setContext('frontendExtraInfo', allExtraInfo)
            scope.setLevel(severityToLevel(severity))
            logger.debug('Sentry error extra info:', allExtraInfo)
            const convertedError = objectToError(error)
            logger.debug('Sentry error converted for report', convertedError)
            resolve(Sentry.captureException(convertedError))
            logger.debug('Error Reported to Sentry successfully')
          } catch (err) {
            reject(err)
          }
        })
      } catch (err) {
        reject(err)
      }
    }).catch((err) => { logger.error(err) })
  } else {
    logger.error('Skipping error report. Either Sentry is disabled or the error was determined to not need to be reported')
    logger.error('Error that was not reported', error)
    logger.error('Extra info passed', extraInfo)
  }

  if (!isPrerendering()) {
    if (process.env.NEXT_PUBLIC_MIXPANEL_TOKEN) {
      try {
        const mixpanel = await getMixpanel()
        mixpanel.track(error.message, {
          sentryErrorId,
          ...allExtraInfo,
        })
      } catch (err) {
        logger.error(err)
      }
    }

    if (process.env.NEXT_PUBLIC_LOGROCKET_APP_ID) {
      try {
        const logrocket = await getLogRocket()
        const logRocketError = objectToError(error)
        if (logRocketError) {
          logrocket.captureException(logRocketError, {
            // TODO: add proper extras. Unfortunately attributes must be primitives
            extra: {},
          })
        }
        // TODO: pass "tags" and "extra"
      } catch (err) {
        logger.error(err)
      }
    }
  }

  return sentryErrorId
}

export async function showErrorReportDialog(data: unknown) : Promise<void> {
  if (process.env.NEXT_PUBLIC_SENTRY_DSN) {
    try {
      const Sentry = await getSentry() as any
      Sentry.showReportDialog(data)
    } catch (error) {
      logger.error(error)
    }
  } else {
    logger.warn('Sentry error report dialog not appearing because Sentry is disabled.')
  }
}
